/* Text-only reconciliation for the default-product non-English route.
 * The authoritative localized Product remains server-rendered.
 * App offers, policies, reviews and unrelated entities are never rewritten as data.
 */
(() => {
  'use strict';
  const source = document.querySelector('script[type="application/ld+json"][data-gbe-localized-product-json]');
  if (!source || source.dataset.gbeReconcileBound === '1') return;
  let reference;
  try { reference = JSON.parse(source.textContent); } catch (_) { return; }
  if (reference['@type'] !== 'Product' ||
      !['name', 'description', 'category', 'url'].every(key => typeof reference[key] === 'string' && reference[key].trim())) return;

  const pageUrl = value => {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      const url = new URL(value, reference.url);
      if (!/^https?:$/.test(url.protocol)) return null;
      return url.origin + url.pathname.replace(/\/+$/, '');
    } catch (_) { return null; }
  };
  const canonical = document.querySelector('link[rel="canonical"]');
  const expectedUrl = pageUrl(reference.url);
  if (!expectedUrl || !canonical || pageUrl(canonical.href) !== expectedUrl || pageUrl(location.href) !== expectedUrl) return;
  source.dataset.gbeReconcileBound = '1';

  const referenceOffers = Array.isArray(reference.offers) ? reference.offers : [reference.offers].filter(Boolean);
  const variantIds = new Set();
  const productSkus = new Set();
  if (reference.sku) productSkus.add(String(reference.sku));
  referenceOffers.forEach(offer => {
    if (offer && offer.sku) productSkus.add(String(offer.sku));
    if (offer && pageUrl(offer.url) === expectedUrl) {
      try {
        const id = new URL(offer.url, reference.url).searchParams.get('variant');
        if (id) variantIds.add(id);
      } catch (_) {}
    }
  });
  const types = node => (Array.isArray(node['@type']) ? node['@type'] : [node['@type']])
    .map(value => typeof value === 'string' ? value.replace(/^https?:\/\/schema\.org\//, '') : '');
  const matchesPage = node => {
    if (typeof node.url === 'string') return pageUrl(node.url) === expectedUrl;
    if (typeof node['@id'] === 'string') {
      try {
        const id = new URL(node['@id'], reference.url);
        if (pageUrl(id.href) === expectedUrl && (id.hash === '#product' || id.hash === '#itempage')) return true;
      } catch (_) {}
    }
    return false;
  };
  const matchesProduct = node => {
    if (matchesPage(node)) return true;
    if (typeof node.url === 'string' && pageUrl(node.url) !== expectedUrl) return false;
    const offers = Array.isArray(node.offers) ? node.offers : [node.offers].filter(Boolean);
    return offers.some(offer => {
      if (!offer || pageUrl(offer.url) !== expectedUrl) return false;
      try {
        const id = new URL(offer.url, reference.url).searchParams.get('variant');
        return id ? variantIds.has(id) : productSkus.has(String(offer.sku || node.sku || ''));
      } catch (_) { return false; }
    });
  };
  const reconcile = () => {
    document.querySelectorAll('script[type="application/ld+json"]').forEach(script => {
      if (script === source) return;
      let data;
      try { data = JSON.parse(script.textContent); } catch (_) { return; }
      let changed = false;
      const pending = [data];
      while (pending.length) {
        const node = pending.pop();
        if (!node || typeof node !== 'object') continue;
        if (!Array.isArray(node)) {
          const nodeTypes = types(node);
          const keys = nodeTypes.includes('Product') && matchesProduct(node)
            ? ['name', 'description', 'category']
            : nodeTypes.includes('ItemPage') && matchesPage(node)
              ? ['name', 'description']
              : [];
          keys.forEach(key => {
            if (node[key] !== reference[key]) {
              node[key] = reference[key];
              changed = true;
            }
          });
        }
        Object.values(node).forEach(value => { if (value && typeof value === 'object') pending.push(value); });
      }
      if (changed) script.textContent = JSON.stringify(data).replace(/</g, '\\u003c');
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', reconcile, { once: true });
  else reconcile();
  const afterLoad = () => {
    reconcile();
    window.setTimeout(reconcile, 1500);
  };
  if (document.readyState === 'complete') window.setTimeout(reconcile, 1500);
  else window.addEventListener('load', afterLoad, { once: true });
})();
